import React, { createContext, useContext, useMemo } from 'react'
import { useDeviceOrientation } from '@renderer/hooks/useDeviceOrientation'
import { computeRawTier, type ResponsiveTier, tierIsCompact, tierIsTiny } from '@renderer/utils/responsiveTier'

export interface ResponsiveTierValue {
  tier: ResponsiveTier
  isCompact: boolean
  isTiny: boolean
  isPortraitViewport: boolean
  isLandscapeViewport: boolean
  viewportWidth: number
  viewportHeight: number
}

const ResponsiveTierContext = createContext<ResponsiveTierValue | null>(null)

export interface ResponsiveTierProviderProps {
  children: React.ReactNode
  /**
   * Scale-to-fit custom sessions: use design canvas size for tiers and layout hooks
   * so they match the fixed `config.screenSize` box inside `ScaleToFit`.
   */
  virtualViewport?: { width: number; height: number }
}

export const ResponsiveTierProvider: React.FC<ResponsiveTierProviderProps> = ({
  children,
  virtualViewport
}) => {
  const physical = useDeviceOrientation()
  const w = virtualViewport?.width ?? physical.viewportWidth
  const h = virtualViewport?.height ?? physical.viewportHeight
  const s = Math.min(w, h)
  const l = Math.max(w, h)
  const tier = computeRawTier(s, l)

  const isPortraitViewport = virtualViewport
    ? virtualViewport.height > virtualViewport.width
    : physical.isPortrait
  const isLandscapeViewport = virtualViewport ? !isPortraitViewport : physical.isLandscape

  const value = useMemo(
    (): ResponsiveTierValue => ({
      tier,
      isCompact: tierIsCompact(tier),
      isTiny: tierIsTiny(tier),
      isPortraitViewport,
      isLandscapeViewport,
      viewportWidth: w,
      viewportHeight: h
    }),
    [tier, isPortraitViewport, isLandscapeViewport, w, h]
  )

  return <ResponsiveTierContext.Provider value={value}>{children}</ResponsiveTierContext.Provider>
}

export function useResponsiveTierContext(): ResponsiveTierValue | null {
  return useContext(ResponsiveTierContext)
}
